import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import OptimizedImage from './OptimizedImage';

const PortraitGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [centerIndex, setCenterIndex] = useState(5); // Start at position 6 (index 5)
  const [isTransitioning, setIsTransitioning] = useState(false);

  const portraits = [
    "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/argqat2o__MG_3894.JPG",
    "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/b1qoe7ri_DDLJ.png",
    "https://customer-assets.emergentagent.com/job_versatile-actress/artifacts/231ntxxm_IMG_5158.JPG",
    "https://customer-assets.emergentagent.com/job_versatile-actress/artifacts/9o1luoz2_Komal%27s%20Potrait.png",
    "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/wl6wqicx_Haryanvi.jpg",
    "https://customer-assets.emergentagent.com/job_029f3d36-c1f5-45c1-8b2c-87cb867c32a3/artifacts/nil7aecj_IMG_6801.JPG",
    "/images/portrait_bw.jpg",
    "https://customer-assets.emergentagent.com/job_029f3d36-c1f5-45c1-8b2c-87cb867c32a3/artifacts/vzffgj2r_IMG_6689.JPG",
    "https://customer-assets.emergentagent.com/job_029f3d36-c1f5-45c1-8b2c-87cb867c32a3/artifacts/tbpy2ma2_DSCF0315.jpg",
    "https://customer-assets.emergentagent.com/job_029f3d36-c1f5-45c1-8b2c-87cb867c32a3/artifacts/xp1rcm3g_IMG-20260323-WA0006.jpg.jpeg",
    "https://customer-assets.emergentagent.com/job_versatile-actress/artifacts/1chdzipb_IMG_8912.JPG",
    "https://customer-assets.emergentagent.com/job_actingfolio/artifacts/vmo2fk6k_Punjabi%20Audition.png"
  ];

  // Helper function to get object position for specific images that need top-anchoring
  const getObjectPosition = (index) => {
    // DDLJ (index 1) and Punjabi Audition (index 11) - anchor to top
    if (index === 1 || index === 11) {
      return 'center top';
    }
    // All other images - default center crop
    return 'center center';
  };

  const handlePrev = () => {
    if (isTransitioning) return; // Prevent multiple clicks during transition
    setIsTransitioning(true);
    const newIndex = centerIndex > 0 ? centerIndex - 1 : portraits.length - 1;
    setCenterIndex(newIndex);
    setTimeout(() => setIsTransitioning(false), 300); // Match transition duration
  };

  const handleNext = () => {
    if (isTransitioning) return; // Prevent multiple clicks during transition
    setIsTransitioning(true);
    const newIndex = centerIndex < portraits.length - 1 ? centerIndex + 1 : 0;
    setCenterIndex(newIndex);
    setTimeout(() => setIsTransitioning(false), 300); // Match transition duration
  };

  return (
    <section id="portraits" className="min-h-screen bg-[#0a0a0a] py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4">
            PROFESSIONAL <span className="text-[#4ECDC4]">PORTRAITS</span>
          </h2>
          <div className="w-24 h-1 bg-[#4ECDC4] mx-auto mb-6"></div>
        </div>

        {/* Filmstrip Layout - MOBILE: Full width, DESKTOP: With blurred sides */}
        <div className="relative max-w-7xl mx-auto overflow-hidden py-8">
          {/* Main Container */}
          <div className="relative flex items-center justify-center gap-4 px-4">
            {/* Left Blurred Image - DESKTOP ONLY */}
            <div className="hidden lg:block flex-shrink-0 w-64 opacity-20 blur-sm">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <OptimizedImage
                  key={`left-${centerIndex}`}
                  src={portraits[centerIndex > 0 ? centerIndex - 1 : portraits.length - 1]}
                  alt="Previous"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: getObjectPosition(centerIndex > 0 ? centerIndex - 1 : portraits.length - 1) }}
                />
              </div>
            </div>

            {/* Center Focused Portrait */}
            <div className="relative flex-shrink-0 w-full max-w-2xl z-10">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#4ECDC4] via-[#FF6B6B] to-[#FFD700] rounded-2xl blur-xl opacity-30"></div>
                <OptimizedImage
                  key={`center-${centerIndex}`}
                  src={portraits[centerIndex]}
                  alt={`Portrait ${centerIndex + 1}`}
                  className="relative w-full h-full object-cover transition-all duration-500"
                  style={{ objectPosition: getObjectPosition(centerIndex) }}
                  priority={true}
                />
              </div>
              
              {/* Counter Badge - MOBILE: Bottom center, DESKTOP: Top right */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 lg:top-4 lg:right-4 lg:bottom-auto lg:left-auto lg:translate-x-0 z-10">
                <span className="inline-block px-4 py-2 bg-black/70 backdrop-blur-sm text-white text-sm font-semibold rounded-full border border-white/30">
                  {centerIndex + 1} / {portraits.length}
                </span>
              </div>
            </div>

            {/* Right Blurred Image - DESKTOP ONLY */}
            <div className="hidden lg:block flex-shrink-0 w-64 opacity-20 blur-sm">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <OptimizedImage
                  key={`right-${centerIndex}`}
                  src={portraits[centerIndex < portraits.length - 1 ? centerIndex + 1 : 0]}
                  alt="Next"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: getObjectPosition(centerIndex < portraits.length - 1 ? centerIndex + 1 : 0) }}
                />
              </div>
            </div>

            {/* Navigation Arrows - MOBILE: Bigger (56px), DESKTOP: Normal (48px) */}
            <button
              onClick={handlePrev}
              disabled={isTransitioning}
              className="absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 w-14 h-14 lg:w-12 lg:h-12 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/80 transition-all border border-white/20 z-20 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous"
            >
              <ChevronLeft className="text-white" size={28} />
            </button>

            <button
              onClick={handleNext}
              disabled={isTransitioning}
              className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 w-14 h-14 lg:w-12 lg:h-12 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/80 transition-all border border-white/20 z-20 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next"
            >
              <ChevronRight className="text-white" size={28} />
            </button>
          </div>

          {/* Dot Navigation Below - MOBILE: Smaller dots, DESKTOP: Original size */}
          <div className="flex justify-center gap-1.5 lg:gap-2 mt-6 lg:mt-8 overflow-x-auto px-4">
            {portraits.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isTransitioning) {
                    setIsTransitioning(true);
                    setCenterIndex(index);
                    setTimeout(() => setIsTransitioning(false), 300);
                  }
                }}
                disabled={isTransitioning}
                className={`h-1.5 lg:h-2 rounded-full transition-all duration-300 flex-shrink-0 disabled:cursor-not-allowed ${
                  index === centerIndex
                    ? 'bg-[#4ECDC4] w-6 lg:w-8'
                    : 'bg-gray-600 w-1.5 lg:w-2 hover:bg-gray-500'
                }`}
                aria-label={`Go to portrait ${index + 1}`}
              />
            ))}
          </div>

          {/* Subtle but clearly clickable CTA */}
          <div className="mt-6 lg:mt-8 flex justify-center">
            <Link
              to="/portfolio"
              className="group inline-flex items-center gap-2 px-5 py-2 lg:px-6 lg:py-2.5 border border-[#FFD700]/50 text-[#FFD700] text-[11px] lg:text-xs uppercase tracking-[0.25em] rounded-full hover:bg-[#FFD700] hover:text-black hover:border-[#FFD700] transition-all duration-300 font-semibold"
              data-testid="portraits-portfolio-cta"
            >
              <span>Explore the full portfolio</span>
              <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-8 right-8 text-white hover:text-[#4ECDC4] transition-colors text-4xl"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default PortraitGallery;
